import { useComputedColorScheme } from '@mantine/core';

/**
 * The Live AI OS logo, in the right colourway for what it sits on.
 *
 * Two artworks exist because one cannot serve both grounds: the light-ground
 * wordmark is near-black text with a deep green mark, which all but vanishes
 * on the dark sidebar; the dark-ground version has off-white text and a
 * brighter green. Choosing between them is the caller's knowledge of its own
 * background, not something the image can work out — hence `on`.
 *
 * Assets live in /public, trimmed of the generous padding the originals came
 * with so `height` means the height of the artwork rather than of empty canvas.
 */

const asset = (file: string) => `${import.meta.env.BASE_URL}${file}`;

interface LogoProps {
  /**
   * The background it sits on. `auto` follows the Mantine colour scheme; use
   * `light` or `dark` where the surface is pinned regardless of scheme, as the
   * sidebar and the marketing pages are.
   */
  on?: 'light' | 'dark' | 'auto';
  /** Symbol only, for tight spaces such as the collapsed sidebar rail. */
  markOnly?: boolean;
  /** Rendered height in pixels; width follows the artwork's proportions. */
  height?: number;
  className?: string;
}

export function Logo({ on = 'auto', markOnly = false, height = 28, className }: LogoProps) {
  const scheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const surface = on === 'auto' ? scheme : on;

  const file = markOnly
    ? surface === 'dark'
      ? 'mark-dark.png'
      : 'mark.png'
    : surface === 'dark'
      ? 'logo-dark.png'
      : 'logo-light.png';

  return (
    <img
      src={asset(file)}
      alt="Live AI OS"
      className={className}
      style={{ display: 'block', height, width: 'auto' }}
    />
  );
}
